import { useEffect, useState } from 'react';

import { Col, Input, Modal, PageHeader, Row, Skeleton, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useHistory, useLocation } from 'react-router';
import { T } from 'react-translator-component';

import * as S from './styled';

import {
  createVirtualGroupChargeboxsAll,
  getVirtualGroupChargerboxes,
  getVirtualGroupsChargeboxes,
} from '../../../../hooks/virtualgroup';
import { Pagination } from '../../../molecules/Pagination/Pagination';

const { Search } = Input;

const VirtualGroupEditChargeboxes = () => {
  const history = useHistory();
  const { pathname, state } = useLocation<any>();
  const [loading, setLoading] = useState(false);

  const [loadingButton, setLoadingButton] = useState(false);
  const [chargeboxesSelected, setChargeboxesSelected] = useState<any>([]);
  const [newChargeboxes, setNewChargeboxes] = useState<any>([]);
  const [chargeBoxSearchValue, setChargeBoxSearchValue] = useState('');

  const [[, id]] = useState(pathname.split('/').reverse());

  const [chargeboxes, setChargeboxes] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetVirtualGroupsChargeboxes,
    pagination: { page: 1, pageSize: 10 },
  });

  const [currentChargeboxes, setcCurrentChargeboxes] = useState<any>();

  const columns: ColumnsType<any> = [
    {
      title: 'Carregador',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'ID',
      dataIndex: 'charge_box_code',
      key: 'charge_box_code',
    },
    {
      title: 'Configurações avançadas',
      dataIndex: 'has_config_tec',
      key: 'has_config_tec',
      align: 'center',
      render: (value) => <S.Bullet active={value} />,
    },
  ];

  const rowSelection = {
    onSelect: (select) => {
      if (state?.payload?.smart_charging?.enabled && !select?.has_config_tec) {
        return Modal.error({
          title:
            'Não é possível adicionar um carregador sem configurações avançadas em uma configuração com smart charging habilitado!',
        });
      }

      setChargeboxesSelected((prevState) => {
        const itemIndex = prevState.findIndex(
          (item) =>
            item.charge_box_id === select.charge_box_id && item.isSelected,
        );

        if (itemIndex > -1) {
          return prevState.filter(
            (item) => item.charge_box_id !== select.charge_box_id,
          );
        }

        return [
          ...prevState,
          {
            ...select,
            isSelected: true,
          },
        ];
      });
      setNewChargeboxes((value) => value.concat(select));
    },
    getCheckboxProps: (record: any) => {
      return {
        disabled: currentChargeboxes?.filter(
          (value) => value.charge_box_id === record.charge_box_id,
        )?.length,
      };
    },
    selectedRowKeys: currentChargeboxes
      ?.map((value) => `chargebox-${value.charge_box_code}`)
      .concat(
        chargeboxesSelected?.map(
          (value) => `chargebox-${value.charge_box_code}`,
        ),
      ),
  };

  const handleEndSelection = async () => {
    try {
      setLoadingButton(true);

      await createVirtualGroupChargeboxsAll(
        newChargeboxes.map((value) => ({
          virtual_group_id: id,
          charge_box_id: value.charge_box_id,
        })),
      );

      modalUpdateChargeboxsSuccess();
    } catch {
      modalUpdateChargeboxsError();
    } finally {
      setTimeout(() => setLoadingButton(false), 2000);
    }
  };

  const modalUpdateChargeboxsSuccess = () => {
    Modal.success({
      title: 'Carregadores adicionados com sucesso!',
      onOk: () => {
        history.push({
          pathname: `/virtual-groups/view/${id}`,
          state: { activeTab: 'virtual-group-tab-2' },
        });
      },
    });
  };

  const modalUpdateChargeboxsError = () => {
    Modal.error({
      title: 'Ocorreu um erro ao adicionar o(s) carregador(es) selecionado(s)!',
    });
  };

  async function tryToGetVirtualGroupsChargeboxes(
    page: number = 1,
    pageSize: number = 10,
    searchValue?: string,
  ) {
    setLoading(true);

    try {
      const { data } = await getVirtualGroupsChargeboxes(
        page,
        pageSize,
        searchValue,
      );

      setChargeboxes((value) => ({
        ...value,
        data: data.content.map((value) => ({
          ...value,
          key: `chargebox-${value.charge_box_code}`,
        })),
        pagination: {
          page,
          pageSize,
          total: data.totalElements,
        },
      }));
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const { data } = await getVirtualGroupChargerboxes(id);

        setcCurrentChargeboxes(data?.content);
      } finally {
        setTimeout(() => setLoading(false), 2000);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      await tryToGetVirtualGroupsChargeboxes();
    })();
  }, []);

  return (
    <S.Wrapper>
      <PageHeader
        onBack={() => {
          history.push(`/virtual-groups/view/${id}`);
        }}
        title={T('Add chargers')}
      />

      <S.Blank>
        <S.Legend>
          Selecione os pontos de recarga que devem fazer parte da configuração{' '}
          <strong>{state?.payload?.name}</strong>
        </S.Legend>

        <Search
          className='search'
          placeholder={'Procure pelo ID ou descrição do carregador'}
          loading={false}
          size='large'
          value={chargeBoxSearchValue}
          onSearch={() =>
            tryToGetVirtualGroupsChargeboxes(
              chargeboxes?.pagination?.page,
              chargeboxes?.pagination?.pageSize,
              chargeBoxSearchValue,
            )
          }
          onChange={(e) => setChargeBoxSearchValue(e.target.value)}
          style={{ width: '50%', marginBottom: 16 }}
        />

        <Skeleton
          loading={loading}
          active
          paragraph={{
            rows: 5,
            width: '100%',
          }}
          title={false}
        >
          <S.TableWrapper>
            <Table
              rowSelection={{
                hideSelectAll: true,
                type: 'checkbox',
                ...rowSelection,
              }}
              dataSource={chargeboxes?.data}
              columns={columns}
              pagination={false}
              locale={{ emptyText: 'Nenhum carregador encontrado' }}
            />
            <Pagination
              currentPage={chargeboxes?.pagination?.page}
              pageSize={chargeboxes?.pagination?.pageSize}
              totalCount={chargeboxes?.pagination?.total}
              onPageChange={(page: number, pageSize: number | undefined) => {
                chargeboxes.request(page, pageSize, chargeBoxSearchValue);
              }}
            />
          </S.TableWrapper>

          <Row justify='space-between' align='middle'>
            <Col md={12}>
              <S.ChargeboxesSelectedText>
                {chargeboxesSelected?.length} pontos de recarga selecionado
                {chargeboxesSelected.length >= 1 ? 's' : ''}
              </S.ChargeboxesSelectedText>
            </Col>
            <Col md={12} style={{ textAlign: 'right' }}>
              <S.Button
                key='submit'
                size='large'
                type='primary'
                onClick={handleEndSelection}
                loading={loadingButton}
              >
                {T('Add chargers')}
              </S.Button>
            </Col>
          </Row>
        </Skeleton>
      </S.Blank>
    </S.Wrapper>
  );
};

export default VirtualGroupEditChargeboxes;
